import clsx from "clsx";
import React from "react";
import { Box, Typography, CardActionArea, Card } from "@mui/material";
import { ExerciseAssetDto } from "@growth-machine-llc/stridist-api-client";
import { WorkoutExercise } from "../workout/types";
import { ThumbnailVideoBadge } from "./ThumbnailVideoBadge";
import { BodypartTypeLabels, MovementPatternTypeLabels } from "./constants";
import { ExercisesMenu } from "./ExercisesMenu";
import SeeMoreNotes from "../training-summary/SeeMoreNotes";
import { BoxProps } from "@udecode/plate-common";
import { exerciseLibraryStyles } from "./exerciseLibraryStyles";
import { makeStyles } from "@mui/styles";
import { ExerciseCategoryBadge } from "./ExerciseCategoryBadge";
import { CategoryTypeLabel } from "./ManageLibraryExercise";
import { ExerciseTagBadge } from "./ExerciseTagBadge";
import { useUpdateLibraryExercise } from "./hooks/useUpdateLibraryExercise";
import { useLocation } from "react-router-dom";
import { COACH_EXERCISE_LIBRARY_PENDING_ROUTE } from "../../routes/routes";
import PendingAvatarWithTooltip from "./PendingAvatarWithTooltip";

const useStyles = makeStyles((theme) => ({
  actionArea: {
    padding: theme.spacing(3),
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },

  category: {
    padding: theme.spacing(0.3, 1.2),
    alignItems: "center",
    height: 18,
    "& > *": {
      fontSize: 12,
    },
  },

  badge: {
    height: 15,
    minWidth: 15,
    "& > *": {
      fontSize: 10,
    },
  },

  badgeContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },

  avatarContainer: {
    pointerEvents: "none",
    "& > *": {
      pointerEvents: "auto",
    },
  },
}));

export interface ExercisesTableCardsViewProps extends BoxProps {
  exerciseInfo: ExerciseAssetDto;
  onSelectedExercise?: (exerciseAsset: ExerciseAssetDto) => void;
}

export function ExercisesTableMobileView(props: ExercisesTableCardsViewProps) {
  const { className, exerciseInfo, onSelectedExercise, ...other } = props;
  const s = exerciseLibraryStyles();
  const classes = useStyles();
  const exercise = JSON.parse(exerciseInfo.content) as WorkoutExercise;

  const location = useLocation();

  const isPendingTab = location.pathname.includes(
    COACH_EXERCISE_LIBRARY_PENDING_ROUTE,
  );

  const { mutate: updateExercise } = useUpdateLibraryExercise();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [movementPatterMenuOpen, setMovementPatterMenuOpen] =
    React.useState(false);
  const [bodypartMenuOpen, setBodypartMenuOpen] = React.useState(false);
  const [tagMenuOpen, setTagMenuOpen] = React.useState(false);

  const handleAvatarTouchEvents = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return (
    <Card className={clsx(s.mobileCard, className)} {...other}>
      <CardActionArea
        className={classes.actionArea}
        onClick={() => onSelectedExercise?.(exerciseInfo)}
      >
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="center" flexShrink={0}>
            <ThumbnailVideoBadge
              size={58}
              border={!!exerciseInfo.coachId}
              video={exercise.video}
            />
          </Box>

          <Box flexGrow={1} display="flex" flexDirection="column">
            <Typography fontWeight={600}>{exercise.title}</Typography>

            <Typography variant="caption" className={s.subText}>
              <SeeMoreNotes rows={2}>{exercise.instructions}</SeeMoreNotes>
            </Typography>

            <Box className={classes.badgeContainer}>
              <ExerciseCategoryBadge
                label={CategoryTypeLabel.MovementPattern}
                selectedItems={exerciseInfo.movementPatterns}
                labelMap={MovementPatternTypeLabels}
                open={movementPatterMenuOpen}
                setOpen={setMovementPatterMenuOpen}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                exercise={exerciseInfo}
                showDashWhenEmpty={false}
                classes={{ category: classes.category, badge: classes.badge }}
                readonly={!exerciseInfo.coachId}
                updateExercise={updateExercise}
              />

              <ExerciseCategoryBadge
                label={CategoryTypeLabel.Bodypart}
                selectedItems={exerciseInfo.bodyparts}
                labelMap={BodypartTypeLabels}
                open={bodypartMenuOpen}
                setOpen={setBodypartMenuOpen}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                exercise={exerciseInfo}
                showDashWhenEmpty={false}
                classes={{ category: classes.category, badge: classes.badge }}
                updateExercise={updateExercise}
                readonly={!exerciseInfo.coachId}
              />
              <ExerciseTagBadge
                selectedTags={exerciseInfo.tags}
                open={tagMenuOpen}
                setOpen={setTagMenuOpen}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                exercise={exerciseInfo}
                showDashWhenEmpty={false}
                classes={{ category: classes.category, badge: classes.badge }}
                readonly={!exerciseInfo.coachId}
              />
            </Box>
          </Box>
          {isPendingTab && exerciseInfo.pendingAssetClientInfDto?.clientId && (
            <Box
              display="flex"
              alignItems="center"
              className={classes.avatarContainer}
              // Prevent avatar interactions from triggering card selection
              onTouchStart={handleAvatarTouchEvents}
              onTouchEnd={handleAvatarTouchEvents}
              onTouchMove={handleAvatarTouchEvents}
            >
              <PendingAvatarWithTooltip
                pendingAssetClientInfo={exerciseInfo.pendingAssetClientInfDto}
              />
            </Box>
          )}
          {exerciseInfo.coachId && (
            <Box onClick={(e) => e.stopPropagation()}>
              <ExercisesMenu
                exerciseId={exerciseInfo.id}
                archived={exerciseInfo.archived}
                pendingApproval={exerciseInfo.isPendingApproval}
                exerciseNotification={exerciseInfo?.notification}
              />
            </Box>
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
}
